import {
  useAddNote,
  useDeleteDocument,
  useDeleteNote,
  useListAllTags,
  useListDocuments,
  useListNotes,
  useListRecordTags,
  useSetDocumentSharing,
  useSetRecordTags,
  useUploadDocument,
  type EntityType,
} from '@myrentalmate/api-client';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState, type ChangeEvent } from 'react';

import { okData, okItems } from '../../lib/apiData';
import { apiErrorMessage } from '../../lib/apiError';
import { fileSize, formatDateTime } from '../../lib/format';
import { useDownloadDocument } from './useDownloadDocument';

/** What a resident's portal can show, once staff share a file on it. */
const SHAREABLE: EntityType[] = ['lease', 'resident', 'work_order', 'inspection'];

interface RecordRef {
  entityType: EntityType;
  entityId: number;
  canManage: boolean;
}

function TagsSection({ entityType, entityId, canManage }: RecordRef) {
  const queryClient = useQueryClient();
  const params = { entity_type: entityType, entity_id: entityId };
  const onRecord = useListRecordTags(params);
  const all = useListAllTags();
  const save = useSetRecordTags({
    mutation: { onSuccess: () => queryClient.invalidateQueries() },
  });
  const names = okData(onRecord.data)?.names ?? [];
  const options = okData(all.data)?.names ?? [];

  return (
    <Stack spacing={1}>
      <Typography variant="h6">Tags</Typography>
      {save.isError && <Alert severity="error">{apiErrorMessage(save.error)}</Alert>}
      {canManage ? (
        <Autocomplete
          multiple
          freeSolo
          size="small"
          options={options}
          value={names}
          onChange={(_, value) => save.mutate({ data: { ...params, names: value } })}
          renderInput={(input) => (
            <TextField
              {...input}
              placeholder={names.length === 0 ? 'Type a tag and press Enter' : 'Add another tag'}
              slotProps={{ htmlInput: { ...input.inputProps, 'aria-label': 'Tags' } }}
            />
          )}
        />
      ) : names.length === 0 ? (
        <Typography color="text.secondary">No tags.</Typography>
      ) : (
        <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 1 }}>
          {names.map((name) => (
            <Chip key={name} size="small" label={name} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function DocumentsSection({ entityType, entityId, canManage }: RecordRef) {
  const queryClient = useQueryClient();
  const params = { entity_type: entityType, entity_id: entityId };
  const list = useListDocuments({ ...params, page_size: 100 });
  const upload = useUploadDocument({
    mutation: { onSuccess: () => queryClient.invalidateQueries() },
  });
  const remove = useDeleteDocument({
    mutation: { onSuccess: () => queryClient.invalidateQueries() },
  });
  const share = useSetDocumentSharing({
    mutation: { onSuccess: () => queryClient.invalidateQueries() },
  });
  const { download, downloadError } = useDownloadDocument();
  const documents = okItems(list.data);
  const error = upload.error ?? remove.error ?? share.error;
  const canShare = canManage && SHAREABLE.includes(entityType);

  function chooseFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = ''; // so choosing the same file again still fires
    if (file) upload.mutate({ data: { ...params, file } });
  }

  return (
    <Stack spacing={1}>
      <Stack direction="row" sx={{ alignItems: 'center' }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6">Documents</Typography>
          <Typography variant="body2" color="text.secondary">
            PDF, JPG, PNG, DOCX or XLSX, up to 25 MB.
          </Typography>
        </Box>
        {canManage && (
          <Button component="label" variant="outlined" size="small" disabled={upload.isPending}>
            {upload.isPending ? 'Uploading…' : 'Upload file'}
            <input
              hidden
              type="file"
              aria-label="Choose a file to upload"
              accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx"
              onChange={chooseFile}
            />
          </Button>
        )}
      </Stack>
      {error && <Alert severity="error">{apiErrorMessage(error)}</Alert>}
      {downloadError && <Alert severity="error">{downloadError}</Alert>}
      {documents.length === 0 ? (
        <Typography color="text.secondary">No documents.</Typography>
      ) : (
        documents.map((d) => (
          <Stack key={d.id} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography sx={{ flexGrow: 1, overflowWrap: 'anywhere' }}>
              {d.file_name}{' '}
              <Typography component="span" variant="body2" color="text.secondary">
                ({fileSize(d.size_bytes)})
              </Typography>{' '}
              {d.shared_with_resident && <Chip size="small" label="Resident Can See" />}
            </Typography>
            {canShare && (
              <Button
                size="small"
                disabled={share.isPending}
                onClick={() =>
                  share.mutate({
                    documentId: d.id,
                    data: { shared: !d.shared_with_resident },
                  })
                }
              >
                {d.shared_with_resident ? 'Stop sharing' : 'Share with resident'}
              </Button>
            )}
            <Button size="small" onClick={() => download(d.id)}>
              Download
            </Button>
            {canManage && (
              <IconButton
                size="small"
                aria-label={`Remove ${d.file_name}`}
                onClick={() => remove.mutate({ documentId: d.id })}
              >
                ×
              </IconButton>
            )}
          </Stack>
        ))
      )}
    </Stack>
  );
}

function NotesSection({ entityType, entityId, canManage }: RecordRef) {
  const queryClient = useQueryClient();
  const params = { entity_type: entityType, entity_id: entityId };
  const list = useListNotes({ ...params, page_size: 100 });
  const [body, setBody] = useState('');
  const add = useAddNote({
    mutation: {
      onSuccess: () => {
        setBody('');
        return queryClient.invalidateQueries();
      },
    },
  });
  const remove = useDeleteNote({
    mutation: { onSuccess: () => queryClient.invalidateQueries() },
  });
  const notes = okItems(list.data);
  const error = add.error ?? remove.error;

  return (
    <Stack spacing={1}>
      <Typography variant="h6">Notes</Typography>
      {error && <Alert severity="error">{apiErrorMessage(error)}</Alert>}
      {canManage && (
        <Stack spacing={1} sx={{ alignItems: 'flex-end' }}>
          <TextField
            label="Add a Note"
            multiline
            minRows={2}
            fullWidth
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <Button
            size="small"
            variant="outlined"
            disabled={!body.trim() || add.isPending}
            onClick={() => add.mutate({ data: { ...params, body: body.trim() } })}
          >
            Save note
          </Button>
        </Stack>
      )}
      {notes.length === 0 ? (
        <Typography color="text.secondary">No notes.</Typography>
      ) : (
        notes.map((n) => (
          <Paper key={n.id} variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction="row" sx={{ alignItems: 'flex-start' }}>
              <Typography sx={{ flexGrow: 1, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {n.body}
              </Typography>
              {canManage && (
                <IconButton
                  size="small"
                  aria-label="Remove this note"
                  onClick={() => remove.mutate({ noteId: n.id })}
                >
                  ×
                </IconButton>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {n.author} · {formatDateTime(n.created_at)}
            </Typography>
          </Paper>
        ))
      )}
    </Stack>
  );
}

/** Tags, documents and notes for any record. The API checks access by the record's type. */
export function RecordExtras(props: RecordRef) {
  return (
    <Stack spacing={4}>
      <TagsSection {...props} />
      <DocumentsSection {...props} />
      <NotesSection {...props} />
    </Stack>
  );
}
